import { useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { useApiMutation } from "../../../utils/api";
import { genericError } from "../../../utils/errors";
import { SubmitButton } from "../../../utils/submit_button";

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

interface LogPumpingModalProps {
  close: () => void;
  refetch: () => void;
  pumping?: { id: number; ended_at: string; amount_ml: number | null };
}

export const LogPumpingModal = ({
  close,
  refetch,
  pumping,
}: LogPumpingModalProps) => {
  const { post, saving } = useApiMutation();
  const [customTime, setCustomTime] = useState<Date | null>(
    pumping ? new Date(pumping.ended_at) : null,
  );
  const [amount, setAmount] = useState(pumping?.amount_ml ?? 60);

  const getInitialTime = () => new Date();
  const selectedTime = customTime ?? getInitialTime();

  const adjustTime = (offset: number) => {
    const base = customTime ?? getInitialTime();
    const minutes = base.getMinutes();
    const absOffset = Math.abs(offset);
    const remainder = minutes % absOffset;

    let nextDate: Date;
    if (remainder !== 0) {
      const adjustBy = offset > 0 ? absOffset - remainder : -remainder;
      nextDate = new Date(base);
      nextDate.setMinutes(minutes + adjustBy);
    } else {
      nextDate = new Date(base);
      nextDate.setMinutes(minutes + offset);
    }
    nextDate.setSeconds(0, 0);
    setCustomTime(nextDate);
  };

  const adjustAmount = (delta: number) => {
    setAmount((prev) => Math.max(0, prev + delta));
  };

  const handleSubmit = () => {
    const payload = { ended_at: selectedTime.toISOString(), amount_ml: amount };
    const request = pumping
      ? post(`/api/user/update_pumping/${pumping.id}`, payload)
      : post("/api/user/create_pumping", payload);

    request.then(({ data }) => {
      if (data.success) {
        refetch();
        close();
      } else {
        genericError();
      }
    });
  };

  return (
    <Offcanvas show onHide={close} placement="bottom" className="h-100">
      <Offcanvas.Header
        closeButton
        className="border-0 pb-0 justify-content-center"
      >
        <Offcanvas.Title className="w-100 text-center fs-32">
          {pumping ? "Edit Pumping" : "Add Pumping"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="text-center py-4">
        <div className="d-flex align-items-center justify-content-center gap-2">
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(-30)}
          >
            <i className="bi bi-chevron-double-left fs-48 fs-m-24" />
          </button>
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(-5)}
          >
            <i className="bi bi-chevron-left fs-48 fs-m-24" />
          </button>

          <div className="pt-4 pb-4 pl-3 pr-3">
            <div className="fs-64 fs-m-32 fw-bold">
              {formatTime(selectedTime)}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(5)}
          >
            <i className="bi bi-chevron-right fs-48 fs-m-24" />
          </button>
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustTime(30)}
          >
            <i className="bi bi-chevron-double-right fs-48 fs-m-24" />
          </button>
        </div>

        <div className="d-flex align-items-center justify-content-center gap-2">
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustAmount(-5)}
          >
            <i className="bi bi-chevron-left fs-48 fs-m-24" />
          </button>

          <div className="pt-4 pb-4 pl-3 pr-3">
            <div className="fs-64 fs-m-32 fw-bold">{amount} ml</div>
          </div>

          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => adjustAmount(5)}
          >
            <i className="bi bi-chevron-right fs-48 fs-m-24" />
          </button>
        </div>

        <div className="d-flex flex-wrap justify-content-center gap-2 mt-2">
          {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((val) => (
            <button
              key={val}
              type="button"
              className={`btn ${amount === val ? "btn-light" : "btn-outline-secondary"}`}
              onClick={() => setAmount(val)}
            >
              {val}
            </button>
          ))}
        </div>

        <div className="d-flex mt-5 justify-content-center container">
          <SubmitButton
            loading={saving}
            className="btn btn-primary btn-lg col-12 col-sm-3 fs-28"
            onClick={handleSubmit}
          >
            Save
          </SubmitButton>
        </div>
        <div className="d-flex mt-5">
          <button
            type="button"
            className="btn btn-text btn-lg px-5 mx-auto text-muted"
            onClick={close}
          >
            Cancel
          </button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};
